import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Camera, Download, Clock, ImageIcon, Phone, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("tr-TR", { year: "numeric", month: "long", day: "numeric" });
  } catch { return iso.slice(0, 10); }
};

const daysLeft = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86400000));
};

const MyPhotos = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const API = process.env.REACT_APP_BACKEND_URL;
  const token = typeof window !== "undefined" ? localStorage.getItem("fotuber_token") : null;

  useEffect(() => {
    api.get("/me/vesikalik")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const downloadOne = async (it) => {
    try {
      const res = await fetch(`${API}${it.download_url}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("İndirilemedi");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${it.code || "fotograf"}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      alert(e.message || "İndirilemedi");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-20" data-testid="my-photos-page">
      <div className="text-xs tracking-[0.3em] uppercase text-[#d4af37] mb-3">Merhaba {user?.name?.split(" ")[0] || "Üye"}</div>
      <h1 className="hero-title text-4xl md:text-6xl mb-4">Fotoğraflarım</h1>
      <p className="text-neutral-400 text-sm md:text-base max-w-2xl mb-10">
        Fotuber Stüdyo'da çektirdiğiniz dijital vesikalık / portre fotoğraflarınız burada 6 ay boyunca saklanır.
        İstediğiniz zaman yüksek çözünürlüklü PNG olarak indirebilirsiniz. 6 ay sonra fotoğraflar otomatik olarak silinir.
      </p>

      {loading ? (
        <div className="border border-neutral-900 rounded-2xl p-10 text-center text-neutral-500" data-testid="my-photos-loading">
          Fotoğraflarınız yükleniyor…
        </div>
      ) : items.length === 0 ? (
        <div className="border border-neutral-900 rounded-2xl p-10 text-center" data-testid="my-photos-empty">
          <ImageIcon className="w-12 h-12 mx-auto text-neutral-700 mb-3" />
          <div className="text-neutral-300 font-serif text-xl mb-2">Henüz dijital fotoğrafınız yok</div>
          <p className="text-neutral-500 text-sm max-w-md mx-auto mb-5">
            Stüdyomuzda bir vesikalık çektirdiğinizde, kayıt sırasında kullandığınız telefon numarasıyla
            burada görürsünüz. Telefonunuz hesabınızda <span className="inline-flex items-center gap-1 text-amber-400"><Phone className="w-3 h-3" /> {user?.phone || "—"}</span> olarak kayıtlı.
          </p>
          <Link to="/randevu-al">
            <Button data-testid="my-photos-cta-booking" className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">
              <Camera className="w-4 h-4 mr-2" /> Randevu Al
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((it) => {
            const left = daysLeft(it.expires_at);
            return (
              <div key={it.id} data-testid={`my-photo-${it.id}`} className="border border-neutral-900 rounded-2xl overflow-hidden bg-neutral-950">
                <div className="aspect-square bg-white relative">
                  <img
                    src={`${API}${it.thumb_url}`}
                    alt={it.spec_label || "vesikalık"}
                    className="w-full h-full object-contain"
                    loading="lazy"
                    data-testid={`my-photo-thumb-${it.id}`}
                  />
                </div>
                <div className="p-4">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#d4af37] mb-1 flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> {it.brand_name || "Fotuber"}
                  </div>
                  <div className="font-serif text-lg text-neutral-100 truncate">{it.spec_label || "Vesikalık"}</div>
                  <div className="text-[11px] text-neutral-500 mt-1 flex items-center gap-2 flex-wrap">
                    <span>#{it.code || it.id.slice(0, 6)}</span>
                    <span>·</span>
                    <span>{fmtDate(it.created_at)}</span>
                  </div>
                  <div className="text-[11px] text-neutral-500 mt-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {left != null ? `${left} gün sonra silinir` : "6 ay geçerli"}
                  </div>
                  <Button
                    onClick={() => downloadOne(it)}
                    data-testid={`my-photo-download-${it.id}`}
                    className="w-full mt-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"
                  >
                    <Download className="w-4 h-4 mr-2" /> İndir
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-10 border border-neutral-900 rounded-2xl p-5 text-xs text-neutral-500">
        <div className="text-neutral-300 font-semibold mb-1">Bilgilendirme</div>
        Fotoğraflarınız KVKK kapsamında yalnızca sizin erişiminize açıktır. Giriş yapmadan erişilemez.
        6 ay sonra fotoğraflarınız otomatik olarak sunucudan silinir. Erken silme talebi için stüdyomuzla iletişime geçebilirsiniz.
      </div>
    </div>
  );
};

export default MyPhotos;
